import {
  hasAcceptedSession,
  hasMountedAccount,
  hasVerifiedCloudAuthority,
  resolveAccountCloudPosture,
  resolveAccountMachineState,
  type AccountAuthorityFacts,
  type AccountCloudPosture,
  type AccountMachineState,
  type AccountSnapshot,
  type SyncSurfaceAction
} from './accountStateMachine.js'
import type { PickerOptionAvailabilityState } from './commandAvailability.js'
import { resolveCapabilities, type Capabilities } from './capabilities.js'
import type { ConnectivityResolution } from './connectivity.js'
import type { SpaceKind } from './contextModel.js'
import { canEditLane, canPersistLane, isDetachedLane, type LaneState } from './laneMachine.js'
import { applyCloudPosturePaletteActionOverlay, selectSyncPaletteActionAvailability } from './paletteActions.js'

export type OrbitalVisualState = {
  memory: 'anonymous' | 'account'
  account: 'none' | 'online' | 'offline' | 'deactivated'
  memoryActivity?: 'settling'
  memoryActivityKey?: number
  accountActivity?: 'running' | 'blocked'
}

export type AccountShelfReadiness = 'unknown' | 'pending' | 'complete' | 'blocked'

export type RuntimePolicyInput = {
  snapshot: AccountSnapshot
  connectivity: ConnectivityResolution
  lane: LaneState
  space: SpaceKind
  syncEngineStatus?: 'idle' | 'pending' | 'syncing' | 'success' | 'error'
}

export type OrbitalAccountActivityInput = {
  account: OrbitalVisualState['account']
  localMemorySettling: boolean
  accountCloudActivityActive: boolean
  accountCloudDebt: boolean
  cloudSyncAfterLocalConsolidationPending: boolean
  syncEngineStatus: 'idle' | 'pending' | 'syncing' | 'success' | 'error'
  accountShelfReadiness: AccountShelfReadiness
}

export type RuntimeSyncAction = {
  action: SyncSurfaceAction
  state: PickerOptionAvailabilityState
}

export type RuntimePolicy = {
  lifecycle: AccountMachineState
  authority: AccountAuthorityFacts
  cloudPosture: AccountCloudPosture
  snapshot: AccountSnapshot
  capabilities: Capabilities
  lane: LaneState
  isDetachedLane: boolean
  canPersistLane: boolean
  canEditLane: boolean
  syncPickerPlaceholder: string
  syncActions: RuntimeSyncAction[]
  orbitalVisualState: OrbitalVisualState
  syncOperationalState: 'local_only' | 'paused' | 'unbound' | 'offline' | 'connected'
}

export type AccountSyncBentoPresentationInput = {
  lifecycle: AccountMachineState
  scheduledEndLabel?: string
  cloudPosture: AccountCloudPosture
  failedAuth?: boolean
}

export type AccountSyncBentoPresentation = {
  syncLabel: string
  networkLabel?: string
  syncDetail?: string
  feedback: string
  description: string
}

// Failed auth is a presentation verdict only: the mounted Account keeps its local
// authority, we just stop calling the cloud connected. It needs a browser that is
// really online and an auth observation that has actually landed - short of that the
// honest story is offline, not a dead session.
export function selectFailedAuthPresentation(input: {
  snapshot: AccountSnapshot
  browserOnline: boolean
  authObservationSettled: boolean
}): boolean {
  const lifecycle = resolveAccountMachineState(input.snapshot)
  return input.authObservationSettled
    && input.browserOnline
    && hasMountedAccount(input.snapshot)
    && input.snapshot.acceptedSessionOwnerId !== input.snapshot.mountedAccountOwnerId
    && (lifecycle === 'sync_active' || lifecycle === 'sync_terminating')
}

export function selectOrbitalVisualState(input: {
  lifecycle: AccountMachineState
  cloudPosture: AccountCloudPosture
}): OrbitalVisualState {
  if (input.lifecycle === 'local_only') {
    return {
      memory: 'anonymous',
      account: 'none'
    }
  }
  if (input.lifecycle === 'sync_deactivated') {
    return {
      memory: 'account',
      account: 'deactivated'
    }
  }
  return {
    memory: 'account',
    account: input.cloudPosture === 'connected-verified' ? 'online' : 'offline'
  }
}

export function resolveOrbitalAccountActivity(
  input: OrbitalAccountActivityInput
): OrbitalVisualState['accountActivity'] {
  if (input.account === 'none') return undefined
  if (input.account === 'deactivated') return undefined
  if (input.localMemorySettling) return undefined
  if (input.accountShelfReadiness === 'blocked') return 'blocked'

  const cloudWorkActive =
    input.accountCloudActivityActive
    || input.syncEngineStatus === 'pending'
    || input.syncEngineStatus === 'syncing'
  const cloudWorkBlocked =
    input.syncEngineStatus === 'error'
    || input.accountCloudDebt
    || input.cloudSyncAfterLocalConsolidationPending

  if (input.account === 'offline') {
    return cloudWorkActive || cloudWorkBlocked ? 'blocked' : undefined
  }
  if (
    cloudWorkActive
    || cloudWorkBlocked
    || input.accountShelfReadiness === 'pending'
  ) return 'running'
  return undefined
}

export function resolveAccountSyncBentoPresentation(
  input: AccountSyncBentoPresentationInput
): AccountSyncBentoPresentation {
  // Failed auth is the one case where the wire is fine and the session is not, so the
  // network row reports the browser rather than the now-unverifiable cloud posture.
  const networkLabel = `network : ${input.failedAuth
    ? 'online'
    : (input.lifecycle === 'local_only'
      ? 'local'
      : (input.cloudPosture === 'connected-verified' ? 'online' : 'offline'))}`
  const syncDetail = input.lifecycle === 'sync_terminating'
    ? (input.scheduledEndLabel ? `date : ${input.scheduledEndLabel}` : undefined)
    : (input.lifecycle === 'sync_active' && input.scheduledEndLabel ? `date : ${input.scheduledEndLabel}` : undefined)
  const syncLabel = input.failedAuth
    ? 'sync : failed auth'
    : (input.lifecycle === 'sync_deactivated'
      ? 'sync : deactivated'
      : (input.lifecycle === 'sync_terminating'
        ? 'sync : ending'
        : (input.lifecycle === 'sync_active' ? 'sync : active' : 'sync : off')))
  const feedback = input.lifecycle === 'sync_deactivated'
    ? 'Account sync is deactivated. Restore it through subscription options or export your Account spaces.'
    : (input.lifecycle === 'sync_terminating'
      ? (input.scheduledEndLabel
        ? `Account sync ends on ${input.scheduledEndLabel}. reactivate to keep it active`
        : 'Account sync is scheduled to end. reactivate to keep it active')
      : (input.lifecycle === 'sync_active'
        ? (input.scheduledEndLabel ? `Account sync renews on ${input.scheduledEndLabel}` : 'Account sync is active')
        : 'memory works without Account'))
  const description = input.lifecycle === 'sync_deactivated'
    ? 'Account sync is deactivated.'
    : (input.lifecycle === 'sync_terminating'
      ? 'Account sync remains active until period end.'
      : (input.lifecycle === 'sync_active'
        ? 'Account sync and backup are active.'
        : 'Account sync and backup are off.'))

  return {
    syncLabel,
    networkLabel,
    syncDetail,
    feedback,
    description
  }
}

export function resolveRuntimePolicy(input: RuntimePolicyInput): RuntimePolicy {
  const snapshot: AccountSnapshot = { ...input.snapshot }
  const authority: AccountAuthorityFacts = {
    mountedAccountOwnerId: snapshot.mountedAccountOwnerId,
    acceptedSessionOwnerId: snapshot.acceptedSessionOwnerId,
    hasVerifiedProfileAuthority: snapshot.hasVerifiedProfileAuthority,
    isCloudReachable: !input.connectivity.isOfflineLike
  }
  const cloudPosture = resolveAccountCloudPosture(authority)
  const lifecycle = resolveAccountMachineState(snapshot)
  const hasSyncLifecycle = lifecycle !== 'local_only'
  const hasSyncAccess =
    snapshot.hasSyncPlan
    && (lifecycle === 'sync_active' || lifecycle === 'sync_terminating')
  const capabilities = resolveCapabilities({
    hasMountedAccount: hasMountedAccount(snapshot),
    hasAcceptedSession: hasAcceptedSession(snapshot),
    hasVerifiedCloudAuthority: hasVerifiedCloudAuthority(snapshot),
    cloudPosture,
    hasSyncAccess,
    isTerminationScheduled: lifecycle === 'sync_terminating',
    isDeactivated: lifecycle === 'sync_deactivated',
    hasUsername: snapshot.hasUsername,
    isSyncPaired: snapshot.isSyncPaired,
    isIndexSpace: snapshot.isIndexSpace,
    isViewMode: snapshot.isViewMode
  })
  const syncActions = applyCloudPosturePaletteActionOverlay(
    selectSyncPaletteActionAvailability(snapshot),
    cloudPosture
  ).map(entry => ({
    action: entry.action,
    state: entry.state
  }))
  const syncOperationalState: RuntimePolicy['syncOperationalState'] = !hasSyncLifecycle
    ? 'local_only'
    : (lifecycle === 'sync_deactivated'
      ? 'paused'
      : (input.space === 'authored' && !snapshot.isSyncPaired
        ? 'unbound'
        : (cloudPosture === 'connected-verified' ? 'connected' : 'offline')))

  return {
    lifecycle,
    authority,
    cloudPosture,
    snapshot,
    capabilities,
    lane: input.lane,
    isDetachedLane: isDetachedLane(input.lane),
    canPersistLane: canPersistLane(input.lane),
    canEditLane: canEditLane(input.lane),
    syncPickerPlaceholder: 'account command list:',
    syncActions,
    orbitalVisualState: selectOrbitalVisualState({
      lifecycle,
      cloudPosture
    }),
    syncOperationalState
  }
}
